import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ICreateShopAction, ICreateShopObject } from 'models/shop/createshop';
import { IStore } from 'redux/IStore';
import { Field, reduxForm, getFormSyncErrors } from 'redux-form';
import { BottomButton, ErrorSummary } from 'components';
import { PortalPageSection } from 'components/PortalStructure';
import { required, maxLength, email, scrollErrorIntoView } from 'helpers/validationattributes';
import { PortalInputField, PortalSelectMenu } from 'components/FormControls';
import { createCountriesOptions, createDomainValueOptions } from 'helpers/common';
import { InjectedRouter, Link } from 'react-router';
import { ICropImageData, ICropImageObject, IImageData, ILanguage } from 'models/common';
import {
	getCountryCallingCodeDomainValues,
	getCountryDomainValues,
	getShopTypeDomainValues,
	getCurrencyDomainValues,
} from 'redux/modules/domainvalues';
import { Dispatch } from 'redux';
import { preventRouterLeave } from 'helpers/hooks';
import { IAddImageData } from 'models/common/addimage';
import { closeModal, showModal } from 'redux/modules/modal';
import { NewImageUpload } from 'components/FormControls/NewImageUpload';
import { IUserLanguage } from 'models/user/userlanguage';

const { connect } = require('react-redux');

type ReduxInjectedProps = Partial<ReturnType<typeof stateToProps> & ReturnType<typeof dispatchToProps>>;

interface IProps extends ReduxInjectedProps {
	location?: any;
	router: InjectedRouter;
	route: any;
	addImageData?: IAddImageData;
	langaugeDomainValues?: ILanguage[];
	userLanguage?: IUserLanguage;

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;

	/* functions props */
	t?: (key: string) => string;
	change?: (field: any, value: any) => void;
	onSubmit?: (form: ICreateShopObject) => void;
	createShopCropImage: (image: ICropImageData) => ICreateShopAction;
}

const maxLength2 = maxLength(2);
// const maxLength50 = maxLength(50);

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class CreateShopStepGeneralForm extends React.Component<IProps> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);

		this.changeImageCrop = this.changeImageCrop.bind(this);
		this.changeImageData = this.changeImageData.bind(this);
		this.confirmImage = this.confirmImage.bind(this);
	}

	imageInputRef = React.createRef();

	public componentDidMount() {
		this.setUserEmail();
		this.props.getCountryDomainValues();
		this.props.getShopTypeDomainValues();
		this.props.getCountryCallingCodeDomainValues();
		this.props.getCurrencyDomainValues();
	}

	public componentDidUpdate(prevProps: IProps): void {
		const { currentFormValues } = this.props;
		this.setUserEmail();

		// clear offices from next step if country was changed
		if (
			currentFormValues?.shop?.fromCountryKey &&
			currentFormValues?.shop?.fromCountryKey != prevProps?.currentFormValues?.shop?.fromCountryKey
		) {
			if (currentFormValues.shop.mainLocation) {
				this.props.change('shop.mainLocation', null);
			}
			if (currentFormValues.shop.offices) {
				this.props.change('shop.offices', null);
			}
		}
	}

	private setUserEmail() {
		const current = this.props.currentFormValues?.shop?.shippingEmail || '';
		const userEmail = this.props.userPreferences?.email || '';

		if (current !== userEmail && userEmail) {
			this.props.change('shop.shippingEmail', userEmail);
		}
	}
	public onSubmit(form: ICreateShopObject) {
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'name':
				return t('Shop_ManageShop_General_Label_SalonName');
			case 'shopTypes':
				return t('Shop_ManageShop_General_Label_ShopType');
			case 'code':
				return t('Shop_ManageShop_General_Label_ShopHandle');
			case 'fromCountryKey':
				return t('Shop_ManageShop_ShippingAddress_Label_CountryCode');
			case 'description':
				return t('Shop_ManageShop_General_Label_Description');
			case 'about':
				return t('Shop_ManageShop_General_Label_About');
			case 'email':
				return t('Shop_ManageShop_ShippingAddress_Label_Email');
			case 'phone':
				return t('Shop_ManageShop_ShippingAddress_Label_Phone');
			default:
				return key;
		}
	}

	private changeImageData(image: IImageData) {
		return new Promise<void>((resolve) => {
			this.props.change('picture', image.href);
			this.props.change('pictureId', image.id);
			resolve();
		});
	}

	private changeImageCrop(cropData: ICropImageObject) {
		return this.props.change('shop.imageCrop', cropData);
	}

	private confirmImage() {
		const { currentFormValues, change } = this.props;
		const { shop } = this.props.currentFormValues;
		change('picture', currentFormValues?.picture);
		change('pictureId', currentFormValues?.pictureId);

		if (!currentFormValues.pictureId) {
			return;
		}
		const cropData: any = {
			...shop.imageCrop,
			id: currentFormValues.pictureId,
		};
		this.props.createShopCropImage(cropData);
	}

	public render() {
		const {
			handleSubmit,
			formErrors,
			submitFailed,
			t,
			location,
			currentFormValues,
			countryDomainValues,
			shopTypeDomainValues,
			countryCallingCodes,
			userLanguage,
			languageDomainValues,
		} = this.props;
		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.createShop ? this.props.createShop.error : false,
			message: this.props.createShop ? this.props.createShop.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const countries = createCountriesOptions(t, countryDomainValues);
		const shopTypes = createDomainValueOptions(t, 'DV_ShopTypes_', shopTypeDomainValues, false);
		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', countryCallingCodes);
		const culture = languageDomainValues?.find((x) => x.key === userLanguage.languageKey).culture;

		return (
			<form onSubmit={handleSubmit(this.onSubmit)}>
				<h1 className="text-center">{t('Shop_ManageShop_General_Label_AddSalonInformation')}</h1>
				<div className="profileError">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<PortalPageSection title={t('Shop_ManageShop_General_Label_SalonInfo')}>
					<Field
						name="shop.name"
						component={PortalInputField}
						type="text"
						autoFocus={true}
						validate={[required]}
						label={t('Shop_ManageShop_General_Label_SalonName')}
					/>
					<Field
						name="shop.shopTypes"
						component={PortalSelectMenu}
						type="text"
						items={shopTypes}
						multi={true}
						validate={[required]}
						isRequired={true}
						label={t('Shop_ManageShop_General_Label_ShopType')}
						searchable={false}
					/>
					<Field
						name={'shop.fromCountryKey'}
						component={PortalSelectMenu}
						items={countries}
						validate={[required, maxLength2]}
						multi={false}
						isRequired={true}
						closeOnSelect={true}
						label={t('Shop_ManageShop_ShippingAddress_Label_CountryCode')}
					/>
				</PortalPageSection>
				<PortalPageSection title={t('Shop_ManageShop_Location_Title_Contact')}>
					<Field
						name="shop.shippingEmail"
						isDisabled={true}
						component={PortalInputField}
						type="text"
						validate={[required, email]}
						isRequired={true}
						label={t('Shop_ManageShop_Offices_ManageOffice_Label_Email')}
					/>
					<div className="row">
						<div className="col-md-4 col-xs-12">
							<Field
								name={'shop.callingCodeKey'}
								component={PortalSelectMenu}
								type="text"
								items={callingCodes}
								multi={false}
								isDisabled={true}
								isRequired={true}
								validate={[required]}
								label={t('BSPortal_Common_Phone_Prefix')}
							/>
						</div>
						<div className="col-md-8 col-xs-12">
							<Field
								name={'shop.phoneNumber'}
								component={PortalInputField}
								isDisabled={true}
								type="text"
								validate={[required]}
								label={t('Shop_ManageShop_Offices_ManageOffice_Label_PhoneNumber')}
							/>
						</div>
					</div>
				</PortalPageSection>
				<PortalPageSection title={t('Shop_ManageShop_General_Label_Image')}>
					<NewImageUpload
						imageId={currentFormValues?.shop?.extraImageId}
						imageUrl={currentFormValues?.shop?.extraImageUrl} // NewImageUpload needs this set to display the cropped image
						imageCrop={currentFormValues?.shop?.imageCrop}
						onChangeCropData={this.changeImageCrop}
						onChangeImageData={this.changeImageData}
						onConfirm={this.confirmImage}
					/>
				</PortalPageSection>
				<BottomButton type="submit">{t('BSPortal_Common_Next')}</BottomButton>
				<div className="createShop__termsOfUse">
					<span>{t('Shop_Policies_TermsOfUse_ShopCreation_Part1')} </span>
					<Link to={'/termsofuse/' + culture?.substring(0, 2)}>{t('Shop_Policies_TermsOfUse_ShopCreation_Part2')}</Link>
				</div>
			</form>
		);
	}
}

let CreateShopStepGeneral = reduxForm({
	form: c.FORM_NAME_CREATE_SHOP,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
	enableReinitialize: true,
	keepDirtyOnReinitialize: true,
	onSubmitFail: () => scrollErrorIntoView(),
})(CreateShopStepGeneralForm);

const stateToProps = (state: IStore) => {
	let currentFormValues: ICreateShopObject = null;
	if (state.form[c.FORM_NAME_CREATE_SHOP] && state.form[c.FORM_NAME_CREATE_SHOP].values) {
		currentFormValues = state.form[c.FORM_NAME_CREATE_SHOP].values;
	} else {
		currentFormValues = state.createShop.createShopObj;
	}

	const initialValues = {
		...currentFormValues,
		shop: {
			...currentFormValues.shop,
			shippingEmail: state.userPreferences?.email || '',
			callingCodeKey: state.userPreferences?.callingCodeKey || '',
			phoneNumber: state.userPreferences?.phoneNumber || '',
		},
	};

	return {
		imageData: state.image,
		userLanguage: state.userLanguage,
		languageDomainValues: state.domainValues.languages,
		initialValues,
		createShop: state.createShop,
		currentFormValues,
		formErrors: getFormSyncErrors(c.FORM_NAME_CREATE_SHOP)(state),
		countryDomainValues: state.domainValues.countries,
		shopTypeDomainValues: state.domainValues.shopType,
		countryCallingCodes: state.domainValues.countryCallingCodes,
		currencyDomainValues: state.domainValues.currencies,
		userPreferences: state.userPreferences,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
	getCountryDomainValues: () => dispatch(getCountryDomainValues()),
	getShopTypeDomainValues: () => dispatch(getShopTypeDomainValues()),
	showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
	closeModal: () => dispatch(closeModal()),
	getCurrencyDomainValues: () => dispatch(getCurrencyDomainValues()),
});

CreateShopStepGeneral = connect(stateToProps, dispatchToProps)(CreateShopStepGeneral);

export { CreateShopStepGeneral };
